import { useContext, useEffect, useReducer } from 'react'

import { type FrameBuilderState, emptyFrameBuilderState } from '../state'
import { frameBuilderStateReducer } from '../state-mutation'
import {
  getIsPassepartoutColorValid,
  getIsPassepartoutSizeValid,
  getIsProfileValid,
  getIsTextureValid,
} from './state-is-valid'
import { FrameBuilderConfigurationContext } from '../../contexts/configuration-context'
import { FrameBuilderSelectedStateContext } from '../../contexts/state-context'

/**
 * Saves last valid frame builder state when validation fails.
 */
export const useLastValidFrameBuilderState = (): FrameBuilderState => {
  const configuration = useContext(FrameBuilderConfigurationContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const [lastValidState, dispatchLastValidFrameBuilderState] = useReducer(
    frameBuilderStateReducer,
    emptyFrameBuilderState,
  )

  // Deselect passepartout size
  useEffect(() => {
    if (
      selectedState.passepartout.size &&
      !getIsPassepartoutSizeValid(
        selectedState.artwork.width,
        selectedState.artwork.height,
        selectedState.passepartout.size,
        selectedState.passepartout.color,
        selectedState.profile,
      )
    ) {
      dispatchLastValidFrameBuilderState({
        type: 'passepartout',
        size: selectedState.passepartout.size,
      })
      dispatchSelectedState({
        type: 'passepartout',
        size: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.passepartout.color,
    selectedState.passepartout.size,
    selectedState.profile,
  ])

  // Reselect passepartout size
  useEffect(() => {
    if (
      lastValidState.passepartout.size &&
      getIsPassepartoutSizeValid(
        selectedState.artwork.width,
        selectedState.artwork.height,
        lastValidState.passepartout.size,
        selectedState.passepartout.color,
        selectedState.profile,
      )
    ) {
      dispatchSelectedState({
        type: 'passepartout',
        size: lastValidState.passepartout.size,
      })
      dispatchLastValidFrameBuilderState({
        type: 'passepartout',
        size: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    lastValidState.passepartout.size,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.passepartout.color,
    selectedState.passepartout.size,
    selectedState.profile,
  ])

  // Deselect passepartout color
  useEffect(() => {
    if (
      selectedState.passepartout.color &&
      !getIsPassepartoutColorValid(
        selectedState.passepartout.size,
        selectedState.passepartout.color,
        selectedState.profile,
      )
    ) {
      dispatchLastValidFrameBuilderState({
        type: 'passepartout',
        color: selectedState.passepartout.color,
      })
      dispatchSelectedState({
        type: 'passepartout',
        color: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    selectedState.passepartout.color,
    selectedState.passepartout.size,
    selectedState.profile,
  ])

  // Reselect passepartout color
  useEffect(() => {
    if (
      lastValidState.passepartout.color &&
      getIsPassepartoutColorValid(
        selectedState.passepartout.size,
        lastValidState.passepartout.color,
        selectedState.profile,
      )
    ) {
      dispatchSelectedState({
        type: 'passepartout',
        color: lastValidState.passepartout.color,
      })
      dispatchLastValidFrameBuilderState({
        type: 'passepartout',
        color: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    lastValidState.passepartout.color,
    selectedState.passepartout.color,
    selectedState.passepartout.size,
    selectedState.profile,
  ])

  // Deselect profile
  useEffect(() => {
    const isProfileSelected = !!selectedState.profile?._id

    if (
      isProfileSelected &&
      !getIsProfileValid(
        selectedState.artwork.width,
        selectedState.artwork.height,
        selectedState.profile,
      )
    ) {
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        profile: selectedState.profile,
      })
      dispatchSelectedState({
        type: 'frameStyle',
        profile: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.profile,
  ])

  // Reselect profile
  useEffect(() => {
    const isProfileSelectedBefore = !!lastValidState.profile?._id

    if (
      isProfileSelectedBefore &&
      getIsProfileValid(
        selectedState.artwork.width,
        selectedState.artwork.height,
        lastValidState.profile,
      )
    ) {
      dispatchSelectedState({
        type: 'frameStyle',
        profile: lastValidState.profile,
      })
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        profile: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    lastValidState.profile,
    selectedState.artwork.height,
    selectedState.artwork.width,
    selectedState.profile,
  ])

  // Deselect texture
  useEffect(() => {
    const currentProfile = selectedState.profile || lastValidState.profile
    const isTextureSelected = !!selectedState.texture?._id

    if (
      isTextureSelected &&
      !getIsTextureValid(currentProfile, selectedState.texture)
    ) {
      const defaultTexture = configuration.textures.find(
        (texture) => texture._id === currentProfile?.defaultTexture?._id,
      )

      // Select default texture
      if (defaultTexture) {
        dispatchSelectedState({
          type: 'frameStyle',
          texture: defaultTexture,
        })
        dispatchLastValidFrameBuilderState({
          type: 'frameStyle',
          texture: undefined,
        })

        return
      }

      // Deselect texture, if there is no default texture
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        texture: selectedState.texture,
      })
      dispatchSelectedState({
        type: 'frameStyle',
        texture: undefined,
      })
    }
  }, [
    configuration.textures,
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    lastValidState.profile,
    selectedState.profile,
    selectedState.texture,
  ])

  // Reselect texture
  useEffect(() => {
    const currentProfile = selectedState.profile || lastValidState.profile
    const isTextureSelectedBefore = !!lastValidState.texture?._id

    if (
      isTextureSelectedBefore &&
      getIsTextureValid(currentProfile, lastValidState.texture)
    ) {
      dispatchSelectedState({
        type: 'frameStyle',
        texture: lastValidState.texture,
      })
      dispatchLastValidFrameBuilderState({
        type: 'frameStyle',
        texture: undefined,
      })
    }
  }, [
    dispatchLastValidFrameBuilderState,
    dispatchSelectedState,
    lastValidState.profile,
    lastValidState.texture,
    selectedState.profile,
    selectedState.texture,
  ])

  return lastValidState
}
